import {
  APP_SOURCES,
  Configuration,
  FormQuestion,
  getEntityRefTarget,
  ReportingItem,
} from "vmd-types";

const isLlmSuggestedSource = (source?: string): boolean =>
  source?.trim().toLowerCase() === APP_SOURCES.LLM_SUGGESTED.toLowerCase();

// #2574 "LLM Suggested" column: every question sourced from "LLM Suggested".
export const getLlmSuggestedQuestions = (
  configuration: Configuration | undefined
): FormQuestion[] =>
  (configuration?.trackingItemForms ?? [])
    .flatMap((blueprint) => blueprint.blocks ?? [])
    .flatMap((block) => block.questions ?? [])
    .filter((question) => isLlmSuggestedSource(question.source));

const normalizeAnswerValue = (value: unknown): string[] => {
  if (value === undefined || value === null) {
    return [];
  }

  return (Array.isArray(value) ? value : [value])
    .flatMap((entry) => String(entry).split("\n"))
    .map((entry) => entry.trim().replace(/,$/, "").trim())
    .filter(Boolean);
};

// Questions with an entityRef store their answer on the bound master-data
// column instead of formData (e.g. distribution channel, technical exposure).
const getEntityRefAnswerValue = (
  item: ReportingItem,
  entityRef: NonNullable<FormQuestion["entityRef"]>
): unknown => {
  const target = getEntityRefTarget(entityRef);

  if (target.entity === "basicItem") {
    return (item.basicItem as Record<string, unknown> | undefined)?.[
      target.column
    ];
  }

  if (target.entity === "reportingItem") {
    return (item as unknown as Record<string, unknown>)[target.column];
  }

  return undefined;
};

// Resolves the answer for every "LLM Suggested" question on this item,
// reading formData or the bound entityRef column as appropriate.
export const getLlmSuggestedAnswerValues = (
  item: ReportingItem,
  llmSuggestedQuestions: FormQuestion[],
  configuration?: Configuration
): string[] =>
  llmSuggestedQuestions.flatMap((question) =>
    normalizeAnswerValue(
      question.entityRef
        ? getEntityRefAnswerValue(item, question.entityRef)
        : item.formData?.[question.id]
    ).map((value) =>
      question.entityRef === "reporting-item.distributionChannel"
        ? (configuration?.distributionChannelDefinition?.find(
            (channel) => channel.id === value
          )?.label ?? value)
        : value
    )
  );

// Values that mean "nothing was actually suggested" even though the field is present.
const LLM_SUGGESTED_EMPTY_VALUES = new Set([
  "null",
  "not selected",
  "n/a",
  "not channel selected",
  "no channel selected",
  "unknown",
  "unknown / to be validated",
]);

const isMeaningfulLlmSuggestedValue = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 && !LLM_SUGGESTED_EMPTY_VALUES.has(normalized);
};

// An item was LLM-suggested if any of its "LLM Suggested"-sourced answers
// hold a meaningful (non-placeholder) value.
export const getLlmSuggestedDisplayValue = (
  llmSuggestedAnswerValues: string[]
): "Yes" | "No" =>
  llmSuggestedAnswerValues.some(isMeaningfulLlmSuggestedValue) ? "Yes" : "No";
